import { Loading, ErrorState } from "@/components/common/States";
import EmptyState from "@/components/common/EmptyState";
import { cn } from "@/lib/utils";

/**
 * columns: [{ key, label, render?(row), className?, mobileHide? }]
 * Desktop -> table, Mobile -> stacked cards.
 */
export default function DataTable({
  columns, rows, loading, error, onRetry, onRowClick,
  empty = { title: "No records found" }, testid = "data-table", rowKey = "id",
}) {
  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (!rows || rows.length === 0) return <EmptyState {...empty} />;

  return (
    <div data-testid={testid}>
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80">
              {columns.map((c) => (
                <th key={c.key} className={cn("text-left font-semibold text-slate-600 px-4 py-3 whitespace-nowrap", c.className)}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row[rowKey]}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                data-testid={`row-${row[rowKey]}`}
                className={cn("border-b border-slate-100 last:border-0 transition-colors", onRowClick && "cursor-pointer hover:bg-teal/5")}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn("px-4 py-3 text-slate-700 align-middle", c.className)}>
                    {c.render ? c.render(row, "row") : (row[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden space-y-3">
        {rows.map((row) => (
          <div
            key={row[rowKey]}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            data-testid={`card-${row[rowKey]}`}
            className={cn("rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-2", onRowClick && "active:bg-teal/5")}
          >
            {columns.filter((c) => !c.mobileHide).map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{c.label}</span>
                <span className="text-sm text-slate-700 text-right min-w-0 truncate">{c.render ? c.render(row, "card") : (row[c.key] ?? "—")}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
